import { useState, useCallback } from "react";

import { getCachedViewJobHint } from "../../../../../../shared/native-modules/tare/getCachedViewJobHint";
import { useTareData } from "../../../../../../shared/native-modules/tare/TareDataContext";
import eventEmitter, { ApplyType, ApplyStatus } from "../../../../../../shared/util/eventEmitter";
import { updateApplyStartCookie } from "../../../../../native-module/updateApplyStartCookie";
import { useViewjobProctorAllocation } from "../../../../../proctor/__generated__/ViewjobProctorContext";
import { RnhpVj } from "../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { getEnvironmentConfig } from "../../../../config/urls";
import { useConfirmInterestContext } from "../../../../context/EmployerOutreachContext";
import { useViewjobOneGraphModel, useDomainUrl } from "../../../../context/ViewJobDataContext";
import { useEmployerOutreachInfo } from "../../../../hooks/useEmployerOutreachInfo";
import { useEmployerOutreachSource } from "../../../../hooks/useEmployerOutreachSource";
import { markJobAsDisliked } from "../../../../lib/markJobAsDisliked";
import { useCloseVJ } from "../../../../lib/useCloseVJ";
import {
  logEmployerOutreachApply,
  logEmployerOutreachCompleteApply,
} from "../../../../util/employerOutreachSignalsLoggingUtil";
import { callRezContactApi } from "../EmployerOutreach/utils/rezContactApi";
import {
  useRecordApplyStartMutation,
  useRecordApplyCompleteMutation,
} from "./__generated__/recordApplyEvents.gqlgenerated";

export enum ConfirmInterestStatus {
  Ready = "READY",
  Submitting = "SUBMITTING",
  Success = "SUCCESS",
  Error = "ERROR",
}

export function useConfirmInterest(jobKey: string) {
  const [status, setStatus] = useState<ConfirmInterestStatus>(ConfirmInterestStatus.Ready);
  const ogModel = useViewjobOneGraphModel();
  const tareData = useTareData();
  const domain = useDomainUrl();
  const confirmInterestContext = useConfirmInterestContext();
  const [recordApplyStart] = useRecordApplyStartMutation();
  const [recordApplyComplete] = useRecordApplyCompleteMutation();
  const { outreachSource, rezContactSource, from: entryPoint } = useEmployerOutreachSource();
  const employerOutreachResult = useEmployerOutreachInfo(jobKey);
  const isFullScreenVJActive = useViewjobProctorAllocation(RnhpVj).value >= 2;

  const resetError = useCallback(() => {
    if (status === ConfirmInterestStatus.Error) {
      setStatus(ConfirmInterestStatus.Ready);
    }
  }, [status]);

  const buildEnvironment = tareData?.appModel?.buildEnvironment;
  const environmentConfig = getEnvironmentConfig(buildEnvironment);
  const rezcowBaseUrl = environmentConfig.rezcowBaseUrl;
  const closeVJ = useCloseVJ();

  const confirmInterest = useCallback(async () => {
    setStatus(ConfirmInterestStatus.Submitting);

    try {
      const hint = await getCachedViewJobHint(jobKey);
      const jobResultTrackingKey = hint?.jobResultTrackingKey;

      if (!jobResultTrackingKey) {
        setStatus(ConfirmInterestStatus.Error);
        return;
      }

      const engagements = ogModel?.employerOutreachEngagement?.engagements;
      const invite = engagements?.[0];

      if (
        !invite ||
        !invite.id ||
        !invite.recipient?.id ||
        !invite.sender?.id ||
        !invite.conversation?.key ||
        !invite.conversation?.initialMessage?.id
      ) {
        setStatus(ConfirmInterestStatus.Error);
        return;
      }

      const result = await callRezContactApi({
        rezcowBaseUrl,
        endpoint: "/api/v1/contacted/interested",
        engagement: invite,
        source: rezContactSource,
      });

      if (!result.success) {
        throw new Error(`Failed to confirm interest: ${result.statusCode}`);
      }

      // Log the apply interaction
      logEmployerOutreachApply({
        employerOutreachSource: outreachSource,
        outreachType: employerOutreachResult?.outreachInfo?.outreachType,
        accountKey: ogModel?.accountKey,
        ctk: ogModel?.ctk,
        jobKey,
        mobtk: ogModel?.mobvjtk,
        isError: false,
        entryPoint,
      });

      // Update apply start cookie
      await updateApplyStartCookie(jobKey, "i", domain);

      await recordApplyStart({
        variables: {
          input: {
            jobResultTrackingKey,
          },
        },
      });

      await recordApplyComplete({
        variables: {
          input: {
            jobResultTrackingKey,
          },
        },
      });

      // Log the complete apply interaction
      logEmployerOutreachCompleteApply({
        employerOutreachSource: outreachSource,
        accountKey: ogModel?.accountKey,
        ctk: ogModel?.ctk,
        jobKey,
        mobtk: ogModel?.mobvjtk,
        isError: false,
        entryPoint,
      });

      await markJobAsDisliked(jobKey, true);
      confirmInterestContext?.markAsConfirmed();
      setStatus(ConfirmInterestStatus.Success);

      eventEmitter.emit("finishApply", {
        jobKeys: [jobKey],
        applyType: ApplyType.FAST_APPLY,
        status: ApplyStatus.CONFIRMED,
      });
      if (isFullScreenVJActive) {
        void closeVJ();
      }
    } catch (error) {
      console.error("Error confirming interest:", error);

      // Log error if the apply flow fails
      logEmployerOutreachApply({
        employerOutreachSource: outreachSource,
        outreachType: employerOutreachResult?.outreachInfo?.outreachType,
        accountKey: ogModel?.accountKey,
        ctk: ogModel?.ctk,
        jobKey,
        mobtk: ogModel?.mobvjtk,
        isError: true,
        entryPoint,
      });

      setStatus(ConfirmInterestStatus.Error);
    }
  }, [
    jobKey,
    domain,
    ogModel,
    isFullScreenVJActive,
    rezcowBaseUrl,
    recordApplyStart,
    recordApplyComplete,
    confirmInterestContext,
    outreachSource,
    rezContactSource,
    entryPoint,
    employerOutreachResult,
    closeVJ,
  ]);

  return {
    confirmInterest,
    status,
    resetError,
  };
}
